import {DateTime} from 'luxon';
import React, {useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {StartAndEndDates} from '../../../models/dates/dateIntervalModel';
import {DatePicker} from './datePicker';

/*
 * Types.
 */

interface DateRangePickerProps {
  calendarWeekStartDay: CalendarWeekDaysEnum;
  selectedStartDate?: DateTime;
  selectedEndDate?: DateTime;

  primaryColor?: string;
  secondaryColor?: string;
  disabledColor?: string;
  backgroundHoverColor?: string;

  onConfirm: HandlerOf<StartAndEndDates>;
  onChange?: HandlerOf<StartAndEndDates>;
  onCancel: Handler;
}

/*
 * Styles.
 */

const DateRangePickerWrap = styled.div`
  display: grid;
  grid-template-areas:
    'startPicker endPicker'
    'cta cta';
  grid-gap: 20px;
  width: 460px;
`;

const StartPicker = styled.div`
  grid-area: startPicker;
  padding: 0 5px 0 10px;
`;

const EndPicker = styled.div`
  grid-area: endPicker;
  padding-right: 0 10px 0 5px;
`;

const Cta = styled.div`
  grid-area: cta;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 10px;
`;

const CancelButton = styled(Button)`
  margin-right: 10px;
`;

/*
 * Component.
 */

export const DateRangePicker = (props: DateRangePickerProps) => {
  const today = useRef(DateTime.local());
  const selectingStartDate = useRef(
    Boolean(
      (props.selectedStartDate && props.selectedEndDate) ||
        (!props.selectedStartDate && !props.selectedEndDate),
    ),
  );
  const [selectedDates, setSelectedDates] = useState({
    startDate: props.selectedStartDate,
    endDate: props.selectedEndDate,
  });
  const [minMonthDate, maxMonthDate] = getFocusedMonths(selectedDates, today.current);
  const [focusedStartMonth, setFocusedStartMonth] = useState(minMonthDate);
  const [focusedEndMonth, setFocusedEndMonth] = useState(maxMonthDate);

  const onSubmit = () => {
    const {startDate, endDate} = selectedDates;
    if (startDate && endDate) {
      props.onConfirm({startDate, endDate});
    }
  };

  const onFocusedStartMonthChange = (date?: DateTime) => {
    if (!date) {
      return;
    }
    setFocusedStartMonth(date);
    if (date.toISODate() === focusedEndMonth?.toISODate()) {
      setFocusedEndMonth(focusedEndMonth?.plus({months: 1}));
    }
  };

  const onFocusedEndMonthChange = (date?: DateTime) => {
    if (!date) {
      return;
    }
    setFocusedEndMonth(date);
    if (date.toISODate() === focusedStartMonth?.toISODate()) {
      setFocusedStartMonth(focusedStartMonth?.minus({months: 1}));
    }
  };

  const onDateSelect = (date: DateTime) => {
    const {startDate} = selectedDates;
    if (selectingStartDate.current || !startDate || date < startDate) {
      setSelectedDates({startDate: date, endDate: undefined});
      selectingStartDate.current = false;
      if (props.onChange) {
        props.onChange({startDate: date, endDate: undefined});
      }
    } else {
      setSelectedDates({startDate, endDate: date.endOf('day')});
      selectingStartDate.current = true;
      if (props.onChange) {
        props.onChange({startDate, endDate: date.endOf('day')});
      }
    }
  };

  return (
    <DateRangePickerWrap>
      <StartPicker data-testid="startMonth">
        <DatePicker
          selectedDates={selectedDates}
          calendarWeekStartDay={props.calendarWeekStartDay}
          onDateSelect={onDateSelect}
          focusedMonth={focusedStartMonth}
          onFocusedMonthChange={onFocusedStartMonthChange}
          hideDifferentMonth
        />
      </StartPicker>
      <EndPicker data-testid="endMonth">
        <DatePicker
          selectedDates={selectedDates}
          calendarWeekStartDay={props.calendarWeekStartDay}
          onDateSelect={onDateSelect}
          focusedMonth={focusedEndMonth}
          onFocusedMonthChange={onFocusedEndMonthChange}
          hideDifferentMonth
        />
      </EndPicker>
      <Cta>
        <CancelButton
          onClick={props.onCancel}
          onDoubleClick={props.onCancel}
          variant={ButtonVariantsEnum.SECONDARY}
          size={VisualSizesEnum.SMALL}
          interactionId="cancel"
        >
          <Trans id="cancel">Cancel</Trans>
        </CancelButton>
        <Button
          onClick={onSubmit}
          onDoubleClick={onSubmit}
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.SMALL}
          isDisabled={!selectedDates.startDate || !selectedDates.endDate}
          interactionId="select"
        >
          <Trans id="select">Select</Trans>
        </Button>
      </Cta>
    </DateRangePickerWrap>
  );
};

/*
 * Helpers.
 */

/*
  To keep left and right calendar in sync:
    - right calendar can't be same or smaller month than left
    - left calendar can't be same or larger month than right
*/
function getFocusedMonths(selectedDates: StartAndEndDates, today: DateTime): [DateTime, DateTime] | [] {
  const {startDate, endDate} = selectedDates;
  const startMonthDate = startDate?.startOf('month');
  const endMonthDate = endDate?.startOf('month');

  if (startMonthDate && endMonthDate) {
    return [
      startMonthDate,
      endMonthDate.equals(startMonthDate) ? endMonthDate.plus({months: 1}) : endMonthDate,
    ];
  }

  if (startMonthDate && !endMonthDate) {
    return [startMonthDate, startMonthDate.plus({months: 1})];
  }

  if (!startMonthDate && endMonthDate) {
    return [endMonthDate.minus({months: 1}), endMonthDate];
  }

  return [today.startOf('month'), today.startOf('month').plus({months: 1})];
}
